import { Ref, forwardRef } from "react"
import * as yup from "yup"
import { Form, FormikProps } from "formik"
import { Formik } from "../../../../../shared/components/forms/SchemaFormik"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import { CreateOrEditArticleFormContent } from "./CreateOrEditArticleFormContent"
import {
    CreateOrEditArticleFormValues,
    ArticleItem,
    MerchantArticleType,
    MerchantArticleStatus,
} from "../merchantNewsItemType"

type CreateOrEditArticleFormProps = Readonly<{
    initialValues?: CreateOrEditArticleFormValues | null
    onSubmit: (formData: ArticleItem) => void
}>
type FormikRef = Ref<FormikProps<CreateOrEditArticleFormValues>>

export const BODY_LIMIT = 2000

export const CreateOrEditArticleForm = forwardRef(
    ({ initialValues, onSubmit }: CreateOrEditArticleFormProps, ref: FormikRef) => {
        const { l10n } = useAppLocalization()

        const defaultInitialValues = {
            subject: "",
            type: MerchantArticleType.NORMAL_ARTICLE,
            status: MerchantArticleStatus.DRAFT,
        }

        const validationSchema = yup.object({
            subject: yup.string().required(l10n.getString("validation-title-required")),
            type: yup.string().required(l10n.getString("validation-type-required")),
            body: yup.string().when("type", {
                is: (type: MerchantArticleType) =>
                    type === MerchantArticleType.NORMAL_ARTICLE || type === MerchantArticleType.PRODUCT_NEWS,
                then: (schema) =>
                    schema.required(l10n.getString("validation-body-required")).test(
                        "is-too-long",
                        l10n.getString("create-or-edit-merchant-article-body-too-long", {
                            charsLimit: BODY_LIMIT,
                        }),
                        (value) => value.length <= BODY_LIMIT,
                    ),
                otherwise: (schema) => schema.notRequired(),
            }),
            product: yup.object().when("type", {
                is: (type: MerchantArticleType) => type === MerchantArticleType.PRODUCT_NEWS,
                then: (schema) => schema.required(l10n.getString("required-field")),
                otherwise: (schema) => schema.notRequired(),
            }),
            permaLink: yup.string().when("type", {
                is: (type: MerchantArticleType) => type === MerchantArticleType.EXTERNAL_ARTICLE,
                then: (schema) => schema.required(l10n.getString("required-field")),
                otherwise: (schema) => schema.notRequired(),
            }),
        })

        function handleSubmit(values: CreateOrEditArticleFormValues) {
            const modifyArticle = {
                ...values,
                publishDate: values.publishDate ? values.publishDate.toISODate() : "",
                expirationDate: values.expirationDate ? values.expirationDate.toISODate() : "",
            }
            onSubmit(modifyArticle)
        }

        return (
            <>
                <Formik<CreateOrEditArticleFormValues>
                    enableReinitialize
                    initialValues={initialValues ? initialValues : defaultInitialValues}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                    innerRef={ref}
                    validateOnBlur={false}
                    validateOnChange={false}
                >
                    <Form>
                        <CreateOrEditArticleFormContent isEditPage={!!initialValues} />
                    </Form>
                </Formik>
            </>
        )
    },
)
